import * as React from "react";
import { Select as SelectPrimitive } from "@base-ui/react/select";
import { CheckIcon, CaretDownIcon, CaretUpIcon } from "@phosphor-icons/react";

import { cn } from "../../lib/utils";
import { layerStyle } from "../../lib/layer-style";

/**
 * Migrated from `@radix-ui/react-select` to Base UI.
 *
 * The largest surface in the burndown — **565 triggers and 947 items across
 * 277 files** — and the most structurally different. Base UI splits Radix's
 * `Content` into `Positioner` + `Popup`, and its `Item` exposes `ItemText` and
 * `ItemIndicator` as separate parts.
 *
 * Four differences carry real risk, and three of them fail silently:
 *
 *  1. **Three Radix-owned CSS variables were consumer APIs here.**
 *     `--radix-select-content-available-height` capped the list,
 *     `--radix-select-trigger-width` matched it to its trigger, and
 *     `--radix-select-trigger-height` sized the popper viewport. An undefined
 *     custom property does not error — it invalidates the entire declaration,
 *     so the cap and the width-match stop applying with nothing missing from
 *     the DOM. They become `--available-height` and `--anchor-width`, which
 *     Base UI sets on the positioner; custom properties inherit, so the popup
 *     reads them.
 *  2. **`itemText` is a local addition** (#3337). When set it is the only text
 *     mirrored into the *closed trigger*, while `children` render the full row
 *     — so a rich two-line option does not widen every trigger using one. Base
 *     UI's `ItemText` is the same idea, and the two-branch shape is kept.
 *  3. **`Viewport` is gone.** Radix nested a scrolling viewport inside the
 *     content; Base UI scrolls the popup itself. The height cap therefore
 *     moves onto the popup rather than a child.
 *  4. **`onValueChange` and `onOpenChange` gain a second argument.** Base UI
 *     passes `eventDetails` alongside the value; forwarding it would widen the
 *     callback every one of the 55 `onValueChange` call sites is typed
 *     against.
 *
 * `z-[1500]` — the popper layer — moves to the positioner through
 * `layerStyle()`, which keeps a caller's own `z-` class winning over the
 * default. See `lib/layer-style.ts` for why the default is inline.
 */

/** The popper layer, above the dialog's 1400. */
const SELECT_Z_INDEX = 1500;

/**
 * Walk the declared tree and collect every `SelectItem`'s `value` → label.
 *
 * Radix's `SelectValue` mirrored the selected item's `ItemText` into the closed
 * trigger automatically. Base UI's renders the **raw value** unless the root is
 * given an `items` map — so without this, a trigger that used to read "Central
 * Valley" reads "central", at all 561 `SelectValue` call sites.
 *
 * The alternative was passing `items` at every call site. This keeps the Radix
 * API instead: the items are already declared in JSX, so the map is derivable.
 * The walk is recursive because `SelectGroup` nests them one level down, and it
 * reads `itemText` first so the #3337 split (trigger text vs row content) keeps
 * working — the trigger gets the short label, never the secondary line.
 */
function collectItemLabels(
  children: React.ReactNode,
  into: Record<string, React.ReactNode> = {},
): Record<string, React.ReactNode> {
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) {
      return;
    }
    if (child.type === SelectItem) {
      const {
        value,
        itemText,
        children: rowChildren,
      } = child.props as SelectItemProps;
      if (typeof value === "string") {
        into[value] = itemText !== undefined ? itemText : rowChildren;
      }
      return;
    }
    const nested = (child.props as { children?: React.ReactNode }).children;
    if (nested !== undefined) {
      collectItemLabels(nested, into);
    }
  });
  return into;
}

type SelectProps = Omit<
  SelectPrimitive.Root.Props<string, false>,
  "onValueChange" | "onOpenChange"
> & {
  /**
   * Narrowed from Base UI's, which passes `eventDetails` as a second argument.
   * Forwarding it would widen the callback all 55 call sites are typed against,
   * for a value none of them want.
   *
   * Declared with **method syntax**, exactly as Radix declared it
   * (`onValueChange?(value: string): void`). That is not cosmetic: method
   * signatures are bivariant under `strictFunctionTypes`, so a caller may pass
   * a handler taking a narrower union — `(mode: CustomerResolutionMode) => void`
   * — and still type-check. Several call sites do. Written as a property-style
   * arrow type instead, those become errors and the migration turns into a
   * sweep of unrelated files.
   */
  onValueChange?(value: string): void;
  onOpenChange?(open: boolean): void;
};

function Select({
  onValueChange,
  onOpenChange,
  children,
  ...props
}: SelectProps) {
  const items = React.useMemo(() => collectItemLabels(children), [children]);

  return (
    <SelectPrimitive.Root
      data-slot="select"
      items={items}
      // Base UI can emit `null` when a selection is cleared; Radix only ever
      // emitted a string, and all 55 call sites are typed for that. Coerced
      // rather than widened, so clearing reads as "no value" the way it always
      // has instead of becoming a new case every caller has to handle.
      onValueChange={(value) => onValueChange?.(value ?? "")}
      onOpenChange={(open) => onOpenChange?.(open)}
      {...props}
    >
      {children}
    </SelectPrimitive.Root>
  );
}

function SelectGroup({ ...props }: SelectPrimitive.Group.Props) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

type SelectValueProps = Omit<SelectPrimitive.Value.Props, "className"> & {
  className?: string;
};

function SelectValue({ ...props }: SelectValueProps) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

type SelectTriggerProps = Omit<SelectPrimitive.Trigger.Props, "className"> & {
  className?: string;
  size?: "sm" | "default";
};

function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}: SelectTriggerProps) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex w-fit items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-8 data-[size=sm]:h-6 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon
        render={<CaretDownIcon className="size-4 opacity-50" />}
      />
    </SelectPrimitive.Trigger>
  );
}

type SelectContentProps = Omit<
  SelectPrimitive.Popup.Props,
  "className" | "side" | "align" | "sideOffset" | "alignOffset"
> &
  Pick<
    SelectPrimitive.Positioner.Props,
    "side" | "align" | "sideOffset" | "alignOffset" | "collisionPadding"
  > & {
    className?: string;
    /**
     * Radix's `position`. Base UI always positions against the anchor, so this
     * is accepted and ignored rather than dropped — two call sites still pass
     * `"popper"`, which is what Base UI already does.
     */
    position?: "popper" | "item-aligned";
  };

function SelectContent({
  className,
  children,
  side,
  // Radix's Select popper aligns to the *start* of its trigger; Base UI's
  // positioner defaults to `center`. Left alone the list is centred on the
  // trigger, so a list wider than its trigger hangs off both edges — the
  // screenshots showed it 10px left, exactly half the width difference.
  align = "start",
  sideOffset = 4,
  alignOffset,
  collisionPadding,
  position: _position,
  style,
  ...props
}: SelectContentProps) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner
        data-slot="select-positioner"
        side={side}
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        collisionPadding={collisionPadding}
        // Base UI defaults this to `true`, which overlaps the list on top of
        // the trigger so the selected row lines up with the trigger's text.
        // Radix's `position="popper"` — which this wrapper always passed —
        // places the list *below* the trigger instead, and that is what all
        // 564 call sites look like today. The screenshots caught it: the
        // trigger simply disappears behind its own dropdown.
        alignItemWithTrigger={false}
        style={layerStyle(SELECT_Z_INDEX, className)}
      >
        <SelectPrimitive.Popup
          data-slot="select-content"
          // Also on the popup, not only the positioner. The popup is
          // `position: relative` (it anchors the scroll arrows), so it is the
          // nearest positioned ancestor of anything inside — and z-index only
          // applies to positioned elements. Radix carried `relative z-[1500]`
          // on the one element; splitting them would leave the popup itself
          // stacking at `auto`.
          //
          // Merged with the caller's `style` rather than spread before it. The
          // rest of the props spread *after* this attribute, so a call site
          // passing any `style` at all would otherwise replace the whole object
          // and take the layer with it — the popup would silently stack at
          // `auto` inside a dialog. The caller still wins on any key it sets.
          style={{ ...layerStyle(SELECT_Z_INDEX, className), ...style }}
          className={cn(
            // `--available-height` and `--anchor-width` are Base UI's names for
            // what Radix called `--radix-select-content-available-height` and
            // `--radix-select-trigger-width`. Both are set on the positioner
            // and inherit down here. The height cap lives on the popup because
            // Base UI has no `Viewport` — the popup is the scroll container.
            "bg-popover text-popover-foreground data-[open]:animate-in data-[ending-style]:animate-out data-[ending-style]:fade-out-0 data-[open]:fade-in-0 data-[ending-style]:zoom-out-95 data-[open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative flex max-h-(--available-height) w-full min-w-[8rem] min-w-(--anchor-width) flex-col origin-(--transform-origin) overflow-hidden rounded-md border shadow-md",
            className,
          )}
          {...props}
        >
          <SelectScrollUpButton />
          {/*
            `List` is the scroll container, not the popup.
            Base UI's scroll arrows are `position: absolute` *within the
            scroller* — put the overflow on the popup and the down-arrow lands
            at the bottom of the scrolled content (measured: 1985px down for a
            655px-tall list) instead of pinned to the visible edge. Radix kept
            its buttons outside a separate Viewport, which is the same shape
            this restores.
          */}
          <SelectPrimitive.List className="scroll-my-1 overflow-x-hidden overflow-y-auto p-1">
            {children}
          </SelectPrimitive.List>
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  );
}

type SelectLabelProps = Omit<SelectPrimitive.GroupLabel.Props, "className"> & {
  className?: string;
};

/** Radix's `SelectLabel` is a *group* label; Base UI names it `GroupLabel`. */
function SelectLabel({ className, ...props }: SelectLabelProps) {
  return (
    <SelectPrimitive.GroupLabel
      data-slot="select-label"
      className={cn("text-muted-foreground px-2 py-1.5 text-xs", className)}
      {...props}
    />
  );
}

type SelectItemProps = Omit<
  SelectPrimitive.Item.Props,
  "className" | "children"
> & {
  className?: string;
  children?: React.ReactNode;
  /**
   * When set, this is the only text mirrored into the closed trigger (and used
   * as the item's primary text). `children` render the full row in the list
   * only — use for previews or secondary layout without widening the trigger.
   */
  itemText?: React.ReactNode;
  /**
   * Radix's spelling of Base UI's `label`: the text used to match this item
   * during keyboard type-ahead, when the rendered row is not plain text. Kept
   * under the old name so the call sites that pass it do not change.
   */
  textValue?: string;
};

function SelectItem({
  className,
  children,
  itemText,
  textValue,
  ...props
}: SelectItemProps) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      label={textValue}
      className={cn(
        "data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className,
      )}
      {...props}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      {/*
        `render={<span />}` is load-bearing twice over. Base UI's `ItemText`
        renders a `<div>` where Radix's rendered a `<span>`: block-level changes
        the row's line box (2px taller per row, which compounds down a list),
        and the row's own
        `*:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2`
        rule stops matching a `<div>` — so the icon-and-label layout inside an
        item silently loses its flex treatment.
      */}
      {itemText !== undefined ? (
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          {/*
            No `className` here, and that is deliberate. #3337 passed
            `text-sm font-medium leading-tight text-foreground` — and Radix's
            `ItemText` **drops className entirely**, rendering a bare
            `<span id="radix-…">`. So that styling has never once applied.

            Base UI's `ItemText` honours it, which would quietly *start*
            applying `leading-tight` and shorten every rich row by 2.5px. The
            screenshots caught it. Preserving today's appearance means leaving
            it off; making it apply is a real visual change and belongs in its
            own PR, where someone can decide whether the original intent was
            right.
          */}
          <SelectPrimitive.ItemText render={<span />}>
            {itemText}
          </SelectPrimitive.ItemText>
          {children}
        </div>
      ) : (
        <SelectPrimitive.ItemText render={<span />}>
          {children}
        </SelectPrimitive.ItemText>
      )}
    </SelectPrimitive.Item>
  );
}

type SelectSeparatorProps = Omit<
  SelectPrimitive.Separator.Props,
  "className"
> & {
  className?: string;
};

function SelectSeparator({ className, ...props }: SelectSeparatorProps) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn("bg-border pointer-events-none -mx-1 my-1 h-px", className)}
      {...props}
    />
  );
}

type SelectScrollButtonProps = Omit<
  SelectPrimitive.ScrollUpArrow.Props,
  "className"
> & {
  className?: string;
};

function SelectScrollUpButton({
  className,
  ...props
}: SelectScrollButtonProps) {
  return (
    <SelectPrimitive.ScrollUpArrow
      data-slot="select-scroll-up-button"
      // Base UI positions its scroll arrows absolutely inside the scroller;
      // Radix's were in flow and therefore full width. `inset-x-0` and the
      // popover background restore that, so the arrow spans the list and
      // hides the row sliding under it rather than sitting as a stray chevron
      // over the first item.
      className={cn(
        "bg-popover inset-x-0 top-0 z-10 flex cursor-default items-center justify-center py-1",
        className,
      )}
      {...props}
    >
      <CaretUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpArrow>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: SelectScrollButtonProps) {
  return (
    <SelectPrimitive.ScrollDownArrow
      data-slot="select-scroll-down-button"
      className={cn(
        "bg-popover inset-x-0 bottom-0 z-10 flex cursor-default items-center justify-center py-1",
        className,
      )}
      {...props}
    >
      <CaretDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownArrow>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
